import { DialogConfig } from '@agent-ds/shared/components/dialog/dialog-config';
import { DialogRef } from '@agent-ds/shared/components/dialog/dialog-ref';
import { CompanyDetail, Job, JobType } from '@agent-ds/shared/interfaces';
import {
  AFTER_AZ_FLOW_STATUSES,
  ALL_GRAD_CATEGORIES,
  JOB_SALARY_TABLE_ITEM,
  JobAzCheckConsultingInfo,
  JobSalaryItemValue,
  SALARY_TABLE_FIELD_KEY_PREFIX,
  SalaryTableItemPrefix,
} from '@agent-ds/shared/interfaces/job-az-check';
import { DynamicField, FormMeta, RowMeta } from '@agent-ds/shared/models';
import { CompanyApiService, DynamicFieldService, JobApiService, MasterApiService } from '@agent-ds/shared/services';
import { buildSalaryTableKey } from '@agent-ds/shared/util/salary-table-key';
import { deepClone, formatAddress } from '@agent-ds/shared/util/util';
import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { SalaryTableRowsService } from '../../../services/salary-table-rows.service';
import {
  clearFixedLateNightOvertimeCategoryValues,
  clearFixedOvertimeCategoryValues,
  forceFixedOvertimeFeeNoneForDeemedWorkingHours,
  formatVariableWorkingHoursSystemDescription,
  groupGradCategoriesByAmount,
  groupGradCategoriesByHoursAndAmount,
  isDeemedWorkingHoursSystemSelected,
  SalaryTableOvertimeGroup,
} from '../../../util/job-salary-table';


type FieldModel = 'job' | 'enterprise';
type RowInputType = 'checkbox' | 'radio' | 'dropdown' | 'textarea' | 'form';

const SALARY_TABLE_KEY_PREFIX = 'salaryTable';
const SALARY_DESCRIPTION_ROW_FIELD_KEY = `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.SALARY_DESCRIPTION}`;
const FIXED_OVERTIME_DESCRIPTION_ROW_FIELD_KEY = `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.FIXED_OVERTIME_DESCRIPTION}`;

/**
 * list/multi-listのdisplayType（dropdown / radio / radio+text / checkbox）を
 * 左側フォームの入力コントロール種別へ変換する。それ以外のfieldTypeは
 * 従来通り整形済み文字列をtextareaで編集する（住所・職種・資格等の複合項目は対象外）。
 */
function resolveRowInputType(definition: DynamicField | undefined): RowInputType {
  if (!definition || (definition.fieldType !== 'list' && definition.fieldType !== 'multi-list')) {
    return 'textarea';
  }
  const displayType = (definition.displayType || 'dropdown').replace(/\+.*/, '');
  if (displayType === 'checkbox' || displayType === 'radio' || displayType === 'dropdown') {
    return displayType;
  }
  return 'dropdown';
}

function cloneRawValue(value: any): any {
  if (Array.isArray(value)) {
    return [...value];
  }
  if (value && typeof value === 'object') {
    return { ...value };
  }
  return value;
}

/**
 * dynamicDataの値はfieldNameによってオブジェクト/配列を持つことがあるため、
 * テンプレートへ渡す前に表示用文字列へ整形する（未対応のオブジェクト形式は[object Object]対策としてJSON文字列化する）
 * jobTypesはjobType1のcode1/code2をラベルへ変換するために使用する（未取得の場合はコードのまま表示する）
 */
function formatFieldValue(fieldKey: string, value: any, jobTypes: JobType[]): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // 本社所在地（企業）
  if (fieldKey === 'address') {
    return formatAddress(value, true);
  }

  // 就業時間（開始〜終了）
  if (fieldKey === 'workingHourRange') {
    return value.from && value.to ? `${value.from}〜${value.to}` : value.from || value.to || '';
  }

  // 職種（code1/code2をjobTypesマスタからラベルへ変換）
  if (fieldKey === 'jobType1') {
    const name1 = jobTypes.length ? (jobTypes.find((t) => t.code === value.code1) || { name: value.code1 }).name : value.code1;
    const name2 = jobTypes.length ? (jobTypes.find((t) => t.code === value.code2) || { name: value.code2 }).name : value.code2;
    return [name1, name2].filter((v) => !!v).join(' / ');
  }

  // セミナー・面接情報（構造化グループ）
  if (fieldKey.indexOf('screeningInfo') === 0) {
    return [value.location, value.address, value.nearestStation, value.duration].filter((v) => !!v).join(' / ');
  }
  if (fieldKey.indexOf('interviewInfo') === 0) {
    return value.description || '';
  }

  // 必須資格等（配列オブジェクト）
  if (fieldKey === 'certificates' && Array.isArray(value)) {
    return value
      .map((certificate: any) => certificate.name)
      .filter((v) => !!v)
      .join('、');
  }

  // チェックボックス群など、配列で保持されるフィールド
  if (Array.isArray(value)) {
    return value.join('、');
  }

  // 上記に該当しない未知のオブジェクト形式
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

interface SalaryTableRowDefinition {
  item: JobSalaryItemValue;
  label: string;
  keys: string[];
}

function buildGradCategoryKeys(prefixes: SalaryTableItemPrefix[]): string[] {
  return prefixes.reduce(
    (keys, prefix) => keys.concat(ALL_GRAD_CATEGORIES.map((category) => buildSalaryTableKey(prefix, category))),
    [] as string[],
  );
}

// salaryTableは卒業種別ごとのキーを持つため、企業確認画面と同じ6項目を1行としてまとめる
const SALARY_TABLE_ROW_DEFINITIONS: SalaryTableRowDefinition[] = [
  { item: JOB_SALARY_TABLE_ITEM.SALARY, label: '月給', keys: buildGradCategoryKeys(['salary_']) },
  {
    item: JOB_SALARY_TABLE_ITEM.SALARY_DESCRIPTION,
    label: '給与補足',
    keys: [
      'salaryDescriptionSalaryByGraduateCategory',
      'salaryDescriptionFixedLateNightOvertime',
      'salaryDescriptionVariableWorkingHoursSystem',
      'salaryDescriptionEditable',
    ],
  },
  { item: JOB_SALARY_TABLE_ITEM.FIXED_OVERTIME_FEE, label: '固定残業代', keys: ['fixedOvertimeFee'] },
  {
    item: JOB_SALARY_TABLE_ITEM.FIXED_OVERTIME_DESCRIPTION,
    label: '固定残業代補足',
    keys: [
      ...buildGradCategoryKeys(['fixedOvertimeHours_', 'fixedOvertimeAmount_']),
      'fixedOvertimeDescriptionNonEditable',
      'fixedOvertimeDescriptionEditable',
    ],
  },
  {
    item: JOB_SALARY_TABLE_ITEM.FIXED_LATE_NIGHT_OVERTIME,
    label: '固定の深夜割増手当',
    keys: ['fixedLateNightOvertimeType', ...buildGradCategoryKeys(['fixedLateNightOvertimeHours_', 'fixedLateNightOvertimeAmount_'])],
  },
  { item: JOB_SALARY_TABLE_ITEM.VARIABLE_WORKING_HOURS_SYSTEM, label: '変形労働時間制', keys: ['variableWorkingHoursSystem'] },
];

function formatOvertimeGroups(groups: SalaryTableOvertimeGroup[]): string {
  return groups
    .map((group) => [group.labels.join('・'), `${group.hours}時間分${group.amount.toLocaleString()}円`].filter((v) => !!v).join(' '))
    .join('／');
}

function formatSalaryTableRowValue(item: JobSalaryItemValue, salaryTable: { [key: string]: any }): string {
  const table = salaryTable || {};
  switch (item) {
    case JOB_SALARY_TABLE_ITEM.SALARY:
      return groupGradCategoriesByAmount(table, 'salary_')
        .map((group) => [group.labels.join('・'), `${group.amount.toLocaleString()}円`].filter((v) => !!v).join(' '))
        .join('／');
    case JOB_SALARY_TABLE_ITEM.SALARY_DESCRIPTION:
      return [
        table.salaryDescriptionSalaryByGraduateCategory,
        table.salaryDescriptionFixedLateNightOvertime,
        table.salaryDescriptionVariableWorkingHoursSystem,
        table.salaryDescriptionEditable,
      ]
        .filter((v) => !!v)
        .join('\n');
    case JOB_SALARY_TABLE_ITEM.FIXED_OVERTIME_FEE:
      return table.fixedOvertimeFee || '';
    case JOB_SALARY_TABLE_ITEM.FIXED_OVERTIME_DESCRIPTION:
      return [
        formatOvertimeGroups(groupGradCategoriesByHoursAndAmount(table, 'fixedOvertimeHours_', 'fixedOvertimeAmount_')),
        table.fixedOvertimeDescriptionNonEditable,
        table.fixedOvertimeDescriptionEditable,
      ]
        .filter((v) => !!v)
        .join('\n');
    case JOB_SALARY_TABLE_ITEM.FIXED_LATE_NIGHT_OVERTIME:
      return [
        table.fixedLateNightOvertimeType,
        formatOvertimeGroups(groupGradCategoriesByHoursAndAmount(table, 'fixedLateNightOvertimeHours_', 'fixedLateNightOvertimeAmount_')),
      ]
        .filter((v) => !!v)
        .join('\n');
    case JOB_SALARY_TABLE_ITEM.VARIABLE_WORKING_HOURS_SYSTEM:
      return table.variableWorkingHoursSystem || '';
    default:
      return '';
  }
}

// 反映時に企業側が値を持たないキーで現在値が残らないようにするため、未定義をnullへ揃える
function pickKeys(source: { [key: string]: any }, keys: string[]): { [key: string]: any } {
  const picked: { [key: string]: any } = {};
  keys.forEach((key) => (picked[key] = source[key] !== undefined ? source[key] : null));
  return picked;
}

interface CompareSectionDefinition {
  title: string;
  model: FieldModel;
  fieldNames: string[];
}

const COMPARE_SECTION_DEFINITIONS: CompareSectionDefinition[] = [
  { title: '概要', model: 'job', fieldNames: ['position', 'tagline'] },
  {
    title: '企業概要',
    model: 'enterprise',
    fieldNames: ['capital', 'salesAmount', 'salesDate', 'numberEmployees', 'establishDate', 'marketName', 'url', 'companyInfo'],
  },
  { title: '本社所在地', model: 'enterprise', fieldNames: ['address'] },
  {
    title: '職務内容',
    model: 'job',
    fieldNames: ['jobType1', 'descriptionMainBody', 'description1Title', 'description1Body', 'description2Title', 'description2Body'],
  },
  {
    title: '勤務先',
    model: 'job',
    fieldNames: [
      'workplace',
      'workplaceRemarks',
      'workplacePrefectures',
      'transfer',
      'firstPlaceOfAssignment',
      'passiveSmokingMeasureType',
      'passiveSmokingMeasureRule',
      'passiveSmokingMeasureRemarks',
    ],
  },
  {
    title: '選考内容',
    model: 'job',
    fieldNames: ['requirement', 'certificates', 'interviewDescription', 'aptitudeTest', 'interviewCount'],
  },
  {
    title: 'アピールポイント',
    model: 'job',
    fieldNames: ['appealMainBody', 'appeal1Title', 'appeal1Body', 'appeal2Title', 'appeal2Body'],
  },
  { title: '選考情報', model: 'job', fieldNames: ['resumeRequiredOnRecommend', 'screeningArea', 'workArea'] },
  {
    title: '性格・タイプ',
    model: 'job',
    fieldNames: ['characterAndType1', 'characterAndType2', 'characterAndType3', 'characterAndType4', 'characterAndType5'],
  },
  { title: '調整フローリンク', model: 'job', fieldNames: ['adjustmentFlowLink'] },
  { title: 'ステータス', model: 'job', fieldNames: ['recruitYear'] },
  {
    title: '採用条件',
    model: 'job',
    fieldNames: [
      'communicationSkill',
      'impressionAndCharm',
      'gender',
      'afterGraduate',
      'postGraduate',
      'universityLevel',
      'academicFieldType',
      'academicCourseType',
      'roninYearType',
      'repeatYearType',
      'internationalStudent',
    ],
  },
  {
    title: '待遇・福利厚生',
    model: 'job',
    // salaryTableの6項目は`salaryTable_`接頭辞付きで指定する（挿入位置は企業確認画面の並びに合わせている）
    fieldNames: [
      'employmentMethod',
      'hasTrial',
      'trialMonth',
      'trialDescription',
      'employmentPeriod',
      'workingHourRange',
      'workingHourComment',
      'breakHour',
      'overtimeType',
      'overtimeHours',
      'overtimeWork',
      'paymentMethod',
      `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.SALARY}`,
      'promotion',
      'bonus',
      `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.SALARY_DESCRIPTION}`,
      `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.FIXED_OVERTIME_FEE}`,
      `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.FIXED_OVERTIME_DESCRIPTION}`,
      'workhourType',
      'hasSubmittedToLaborOfficeForDeemedWorkingHours',
      `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.FIXED_LATE_NIGHT_OVERTIME}`,
      'monthlyScheduledWorkingHours',
      'annualScheduledWorkingHours',
      `${SALARY_TABLE_FIELD_KEY_PREFIX}${JOB_SALARY_TABLE_ITEM.VARIABLE_WORKING_HOURS_SYSTEM}`,
      'annualHolidays',
      'weekendDescription',
      'insurance',
      'commuterDescription',
      'supplementDescription',
      'benefitDescription',
    ],
  },
  {
    title: 'セミナー・面接情報',
    model: 'job',
    fieldNames: ['screeningInfoSeminar', 'screeningInfo1', 'interviewInfoSeminar', 'interviewInfo1'],
  },
  { title: 'その他', model: 'job', fieldNames: ['secondMatchTarget'] },
];

/**
 * dynamicData直下ではない項目の読み書き先。container全体ではなくkeysのみを書き換える。
 * - isGroup=false: dynamicData[containerKey][keys[0]] のスカラー値を1行として扱う（勤務地(雇入れ直後)）
 * - isGroup=true : dynamicData[containerKey] のkeys群をまとめて1行として扱う（salaryTableの6行）
 */
interface RowValueTarget {
  containerKey: string;
  keys: string[];
  isGroup: boolean;
}

interface CompareRow {
  fieldKey: string;
  model: FieldModel;
  label: string;
  inputType: RowInputType;
  options: any[];
  currentValue: string;
  companyValue: string;
  currentValueRaw: any;
  companyValueRaw: any;
  target?: RowValueTarget;
  formMeta?: FormMeta;
}

interface CompareSection {
  title: string;
  rows: CompareRow[];
}

@Component({
  selector: 'ag-job-az-check-dialog-apply-company-changes',
  templateUrl: './job-az-check-dialog-apply-company-changes.component.html',
  styleUrls: ['./job-az-check-dialog-apply-company-changes.component.scss'],
  providers: [SalaryTableRowsService],
})
export class JobAzCheckDialogApplyCompanyChangesComponent implements OnInit {
  job: Job;
  companyData: CompanyDetail | null = null;
  private jobTypes: JobType[] = [];
  isUpdating: boolean = false;

  // 企業から返却された修正内容を反映する実データが揃うまでは右側は空欄のまま
  compareSections: CompareSection[] = [];

  // salaryTableの編集用ワーキングコピー
  editingModel: { salaryTable: { [key: string]: any } } = { salaryTable: {} };

  private salaryTableFormRowsByLabel: { [label: string]: RowMeta } = {};

  // 企業からの相談内容（求人ステータスが「募集中」「推薦停止」「終了」の場合は募集中ルート用の保存先を参照する）
  get companyConsultingInfo(): JobAzCheckConsultingInfo {
    return AFTER_AZ_FLOW_STATUSES.includes(this.job.dynamicData.status)
      ? this.job.dynamicData.azConsultingInfoAfterAzOnCompanyCheck
      : this.job.dynamicData.azConsultingInfoOnCompanyCheck;
  }

  constructor(
    public dialog: DialogRef,
    public config: DialogConfig<{ job: Job }>,
    private dynamicService: DynamicFieldService,
    private jobApiService: JobApiService,
    private companyApiService: CompanyApiService,
    private masterApiService: MasterApiService,
    private salaryTableRowsService: SalaryTableRowsService,
  ) {
    if (!config.data || !config.data.job) {
      this.dialog.close();
      return;
    }
    this.job = config.data.job;
    this.salaryTableRowsService.init(() => this.editingModel.salaryTable, SALARY_TABLE_KEY_PREFIX);
  }

  ngOnInit(): void {
    forkJoin([
      this.companyApiService.getDetail(this.job.enterpriseId),
      this.masterApiService.getJobTypes(),
      this.jobApiService.getCompanyAzCheckInfo(this.job.id),
    ]).subscribe(([companyData, jobTypes, info]) => {
      this.companyData = companyData;
      this.jobTypes = jobTypes.flatten((subType, level) => (subType['_selectLevel'] = level), 'jobTypes');

      const companyDynamicDataByModel: { [key in FieldModel]: any } = {
        job: info.jobDynamicData || {},
        enterprise: info.enterpriseDynamicData || {},
      };

      this.editingModel = { salaryTable: deepClone(this.job.dynamicData.salaryTable || {}) };
      this.initSalaryTableForm();

      // 差分のある項目のみ表示する
      this.compareSections = COMPARE_SECTION_DEFINITIONS.map((section) => {
        const currentSourceData = section.model === 'job' ? this.job.dynamicData : this.companyData.dynamicData;
        const companySourceData = companyDynamicDataByModel[section.model];
        const rows = section.fieldNames
          .map((fieldName) => this.buildRow(section.model, fieldName, currentSourceData, companySourceData))
          .filter((row) => this.hasDifference(row));
        return { title: section.title, rows };
      })
        .filter((section) => section.rows.length > 0);

      this.syncFixedOvertimeFeeDisabledState();
      this.salaryTableRowsService.syncVisibility();
    });
  }

  private syncFixedOvertimeFeeDisabledState(): void {
    this.salaryTableRowsService.setFixedOvertimeFeeDisabled(isDeemedWorkingHoursSystemSelected(this.getEditingWorkhourType()));
  }

  // みなし労働時間制へ変更した際は固定残業代を「無」へ強制する
  private applyDeemedWorkingHoursSystemConstraint(): void {
    if (isDeemedWorkingHoursSystemSelected(this.getEditingWorkhourType())) {
      forceFixedOvertimeFeeNoneForDeemedWorkingHours(this.editingModel.salaryTable);
      this.refreshSalaryDescriptionRow();
      // 固定残業代を'無'に強制した際に固定残業代補足（非編集テキスト）もクリアされるため、文字数上限バリデーションを更新する
      this.refreshFixedOvertimeDescriptionRow();
      this.salaryTableRowsService.syncVisibility();
    }
    this.syncFixedOvertimeFeeDisabledState();
  }

  // workhourTypeも左側で編集され得るため、行がある場合は編集中の値を見る
  private getEditingWorkhourType(): string | null {
    const row = this.findRowByFieldKey('workhourType');
    return row ? row.currentValueRaw : this.job.dynamicData.workhourType;
  }

  private findRowByFieldKey(fieldKey: string): CompareRow | undefined {
    for (const section of this.compareSections) {
      const row = section.rows.find((r) => r.fieldKey === fieldKey);
      if (row) {
        return row;
      }
    }
    return undefined;
  }

  // salaryTableの6行分のフォームを生成し、種別追加・給与補足への反映ボタンの実処理を紐付ける
  private initSalaryTableForm(): void {
    const definition = this.dynamicService.getDefinition('job', 'salaryTable');
    if (!definition) {
      return;
    }
    const rows = this.dynamicService.getFormRows(definition);
    this.salaryTableRowsService.rows = rows;
    this.salaryTableRowsService.wire();
    this.salaryTableRowsService.updateDescriptionValidation();
    this.salaryTableFormRowsByLabel = {};
    rows.forEach((row) => {
      // フォームが独立している影響で、紐付け（linkTo）があるとワーニングが出るため外す
      row.fields.forEach((field) => (field.linkTo = undefined));
      if (!row.title) {
        return;
      }
      this.salaryTableFormRowsByLabel[row.title] = row;
    });
  }

  // 表示対象（差分あり）の行かどうかを判定する
  private hasDifference(row: CompareRow): boolean {
    if (row.target && row.target.isGroup) {
      return !this.isRowApplied(row);
    }
    return row.currentValue !== row.companyValue;
  }

  // salaryTable配下の項目と勤務地（workplace）は、dynamicData直下のスカラー値ではないため専用の組み立てを行う
  private buildRow(model: FieldModel, fieldName: string, currentSourceData: any, companySourceData: any): CompareRow {
    const salaryTableDefinition = SALARY_TABLE_ROW_DEFINITIONS.find(
      (rowDefinition) => `${SALARY_TABLE_FIELD_KEY_PREFIX}${rowDefinition.item}` === fieldName,
    );
    if (salaryTableDefinition) {
      return this.buildSalaryTableRow(model, salaryTableDefinition, currentSourceData, companySourceData);
    }
    if (fieldName === 'workplace') {
      return this.buildWorkplaceAddress1Row(model, currentSourceData, companySourceData);
    }

    const definition = this.dynamicService.getDefinition(model, fieldName);
    const rawCurrentValue = currentSourceData[fieldName];
    const rawCompanyValue = companySourceData[fieldName];
    return {
      fieldKey: fieldName,
      model: model,
      label: (definition && definition.label) || fieldName,
      inputType: resolveRowInputType(definition),
      options: (definition && definition.validationStyle && definition.validationStyle.options) || [],
      currentValue: formatFieldValue(fieldName, rawCurrentValue, this.jobTypes),
      companyValue: formatFieldValue(fieldName, rawCompanyValue, this.jobTypes),
      currentValueRaw: cloneRawValue(rawCurrentValue),
      companyValueRaw: rawCompanyValue,
    };
  }

  // 左側は卒業種別ごとに入力欄数が変わるため、求人票詳細と同じag-dynamic-formで編集させる
  private buildSalaryTableRow(
    model: FieldModel,
    rowDefinition: SalaryTableRowDefinition,
    currentSourceData: any,
    companySourceData: any,
  ): CompareRow {
    const companySalaryTable = companySourceData.salaryTable || {};
    const formRow = this.salaryTableFormRowsByLabel[rowDefinition.label];
    return {
      fieldKey: `${SALARY_TABLE_FIELD_KEY_PREFIX}${rowDefinition.item}`,
      model: model,
      label: rowDefinition.label,
      inputType: 'form',
      options: [],
      currentValue: formatSalaryTableRowValue(rowDefinition.item, currentSourceData.salaryTable || {}),
      companyValue: formatSalaryTableRowValue(rowDefinition.item, companySalaryTable),
      currentValueRaw: null,
      companyValueRaw: pickKeys(companySalaryTable, rowDefinition.keys),
      target: { containerKey: 'salaryTable', keys: rowDefinition.keys, isGroup: true },
      formMeta: { groups: formRow ? [{ class: 'no-title-column', rows: [formRow] }] : [] },
    };
  }

  /**
   * 勤務地は求人票詳細・AZチェック・企業確認画面のいずれもaddress1のみを入力対象とする運用のため、address1単独の行として扱う。
   * formatAddressは郵便番号未入力時に空文字を返すため使用しない（zipを入力しない勤務地では左右とも空文字になってしまう）。
   */
  private buildWorkplaceAddress1Row(model: FieldModel, currentSourceData: any, companySourceData: any): CompareRow {
    const currentWorkplace = currentSourceData.workplace || {};
    const companyWorkplace = companySourceData.workplace || {};
    return {
      fieldKey: 'workplace',
      model: model,
      label: '勤務地(雇入れ直後)',
      inputType: 'textarea',
      options: [],
      currentValue: currentWorkplace.address1 || '',
      companyValue: companyWorkplace.address1 || '',
      currentValueRaw: currentWorkplace.address1 !== undefined ? currentWorkplace.address1 : null,
      companyValueRaw: companyWorkplace.address1 !== undefined ? companyWorkplace.address1 : null,
      target: { containerKey: 'workplace', keys: ['address1'], isGroup: false },
    };
  }

  // フォームが項目ごとに独立しており本来連動する項目が自動で変わらないため、手動で連動させる
  onSalaryTableFormChanged(key: string): void {
    const salaryTable = this.editingModel.salaryTable;
    if (key === `${SALARY_TABLE_KEY_PREFIX}.fixedOvertimeFee` && salaryTable.fixedOvertimeFee === '無') {
      clearFixedOvertimeCategoryValues(salaryTable);
      this.salaryTableRowsService.syncVisibility();
      this.refreshSalaryDescriptionRow();
      // 固定残業代補足（非編集テキスト）もクリアされるため、文字数上限バリデーションを更新する
      this.refreshFixedOvertimeDescriptionRow();
    } else if (key.indexOf(`${SALARY_TABLE_KEY_PREFIX}.fixedOvertimeDescription`) === 0) {
      // 「反映」「短縮反映」「クリア」ボタン押下、または固定残業代補足の編集可能欄への入力があった際に文字数上限バリデーションを更新する
      this.refreshFixedOvertimeDescriptionRow();
    } else if (key === `${SALARY_TABLE_KEY_PREFIX}.fixedLateNightOvertimeType` && salaryTable.fixedLateNightOvertimeType !== '有') {
      clearFixedLateNightOvertimeCategoryValues(salaryTable);
      this.salaryTableRowsService.syncVisibility();
      this.refreshSalaryDescriptionRow();
    } else if (key === `${SALARY_TABLE_KEY_PREFIX}.variableWorkingHoursSystem`) {
      salaryTable.salaryDescriptionVariableWorkingHoursSystem = formatVariableWorkingHoursSystemDescription(
        salaryTable.variableWorkingHoursSystem,
      );
      this.refreshSalaryDescriptionRow();
    } else if (key.indexOf(`${SALARY_TABLE_KEY_PREFIX}.salaryDescription`) === 0) {
      this.refreshSalaryDescriptionRow();
    }
  }

  // 反映はsalaryTableを直接書き換えるため、6行すべてを再描画して表示を実データに合わせる
  private onSalaryTableApplied(): void {
    this.salaryTableRowsService.updateDescriptionValidation();
    this.refreshSalaryTableRows();
    this.salaryTableRowsService.syncVisibility();
  }

  private refreshSalaryDescriptionRow(): void {
    this.salaryTableRowsService.updateDescriptionValidation();
    this.refreshSalaryTableRows([SALARY_DESCRIPTION_ROW_FIELD_KEY]);
  }

  private refreshFixedOvertimeDescriptionRow(): void {
    this.salaryTableRowsService.updateDescriptionValidation();
    this.refreshSalaryTableRows([FIXED_OVERTIME_DESCRIPTION_ROW_FIELD_KEY]);
  }

  /**
   * formMetaを作り直してag-dynamic-formを再描画させる。
   * 給与補足の読取専用欄はsupplierで組み立てており、salaryTableを直接書き換えても再評価されないため必要。
   * @param fieldKeys 対象の行。省略時はsalaryTableの全行
   */
  private refreshSalaryTableRows(fieldKeys?: string[]): void {
    this.compareSections.forEach((section) => {
      section.rows.forEach((row) => {
        if (row.inputType !== 'form' || !row.formMeta) {
          return;
        }
        if (fieldKeys && fieldKeys.indexOf(row.fieldKey) < 0) {
          return;
        }
        row.formMeta = { ...row.formMeta, groups: [...row.formMeta.groups] };
      });
    });
  }

  close(): void {
    this.dialog.close();
  }

  // 反映ボタン：企業側の値を現在の求人内容側へコピーする（マージはせず、常に上書き）
  applyCompanyValue(row: CompareRow): void {
    if (row.target && row.target.isGroup) {
      const container = this.editingModel[row.target.containerKey];
      row.target.keys.forEach((key) => (container[key] = row.companyValueRaw[key]));
      this.onSalaryTableApplied();
      return;
    }
    row.currentValue = row.companyValue;
    row.currentValueRaw = cloneRawValue(row.companyValueRaw);
    this.onRowValueChanged(row);
  }

  // 左右の値が一致している間は反映済み（反映ボタンを無効化）とみなす。
  // 反映後にユーザーが左側を編集して値がずれれば、再度活性化する。
  isRowApplied(row: CompareRow): boolean {
    // salaryTableは編集中のワーキングコピーとキーごとに比較
    if (row.target && row.target.isGroup) {
      const container = this.editingModel[row.target.containerKey] || {};
      return row.target.keys.every((key) => this.rawValuesEqual(container[key], row.companyValueRaw ? row.companyValueRaw[key] : null));
    }
    // チェックボックス（複数選択）は選択順序が変わり得るため、整形済みテキストの一致ではなく集合として比較する
    if (Array.isArray(row.currentValueRaw) || Array.isArray(row.companyValueRaw)) {
      return this.arraysEqualAsSets(row.currentValueRaw, row.companyValueRaw);
    }
    return row.currentValue === row.companyValue && this.rawValuesEqual(row.currentValueRaw, row.companyValueRaw);
  }

  // null/undefined/空文字は同一視する（片方が''、もう片方がnullの空欄同士でも一致とみなすため）
  private rawValuesEqual(a: any, b: any): boolean {
    const normalize = (v: any) => (v === null || v === undefined || v === '' ? '' : v);
    return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
  }

  private arraysEqualAsSets(a: any, b: any): boolean {
    const arrA: any[] = Array.isArray(a) ? a : [];
    const arrB: any[] = Array.isArray(b) ? b : [];
    if (arrA.length !== arrB.length) {
      return false;
    }
    const sortedA = [...arrA].sort();
    const sortedB = [...arrB].sort();
    return JSON.stringify(sortedA) === JSON.stringify(sortedB);
  }

  optionValue(option: any): any {
    return option && typeof option === 'object' ? (option.value !== undefined ? option.value : option.code) : option;
  }

  optionLabel(option: any): string {
    return option && typeof option === 'object' ? option.label || option.name || String(this.optionValue(option)) : String(option);
  }

  isOptionSelected(valueRaw: any, option: any): boolean {
    const value = this.optionValue(option);
    return Array.isArray(valueRaw) ? valueRaw.includes(value) : valueRaw === value;
  }

  toggleOption(row: CompareRow, option: any): void {
    const value = this.optionValue(option);
    const current: any[] = Array.isArray(row.currentValueRaw) ? [...row.currentValueRaw] : [];
    const index = current.indexOf(value);
    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(value);
    }
    this.setCurrentValueRaw(row, current);
  }

  // radio/dropdown選択時の共通ハンドラ
  selectOptionValue(row: CompareRow, value: any): void {
    this.setCurrentValueRaw(row, value);
  }

  // currentValueRawとcurrentValue（整形済み表示テキスト）を同時に更新する。
  // isRowApplied()はこの2つを比較するため、片方だけ更新すると反映ボタンの状態が食い違う。
  private setCurrentValueRaw(row: CompareRow, valueRaw: any): void {
    row.currentValueRaw = valueRaw;
    row.currentValue = formatFieldValue(row.fieldKey, valueRaw, this.jobTypes);
    this.onRowValueChanged(row);
  }

  // 編集・反映のどちらで値が変わってもsalaryTable側へ連動させる
  private onRowValueChanged(row: CompareRow): void {
    if (row.fieldKey === 'workhourType') {
      this.applyDeemedWorkingHoursSystemConstraint();
    }
  }

  // 生値がオブジェクト/配列の項目（住所・就業時間等）はcurrentValueRawへ同期しない
  onFreeTextChanged(row: CompareRow, value: string): void {
    row.currentValue = value;
    if (row.currentValueRaw === null || row.currentValueRaw === undefined || typeof row.currentValueRaw !== 'object') {
      row.currentValueRaw = value;
    }
  }

  // 更新ボタン：画面に表示されている現在値をjob/enterpriseそれぞれのdynamicDataへ反映してから保存する
  update(): void {
    this.compareSections.forEach((section) => {
      section.rows.forEach((row) => {
        const target = row.model === 'job' ? this.job.dynamicData : this.companyData.dynamicData;
        if (!row.target) {
          target[row.fieldKey] = row.currentValueRaw;
          return;
        }
        if (row.target.isGroup) {
          return;
        }
        // 対象キーのみを書き換える（workplace.prefecture等、この画面に表示していない項目を消さないため）
        if (!target[row.target.containerKey]) {
          target[row.target.containerKey] = {};
        }
        const container = target[row.target.containerKey];
        container[row.target.keys[0]] = row.currentValueRaw;
      });
    });

    // salaryTableを持たない求人へ空オブジェクトを書き込むと、api側のsyncLegacySalaryFieldsが走って
    // dynamicData直下の月給・給与補足を空で上書きしてしまうため、中身がある場合のみ書き戻す
    if (this.job.dynamicData.salaryTable || Object.keys(this.editingModel.salaryTable).length > 0) {
      this.job.dynamicData.salaryTable = { ...(this.job.dynamicData.salaryTable || {}), ...this.editingModel.salaryTable };
    }

    this.isUpdating = true;
    forkJoin([this.saveJobData(), this.saveCompanyData()]).subscribe({
      next: () => {
        this.isUpdating = false;
        this.jobApiService.refreshEvent.emit();
        this.dialog.close();
      },
      error: (error) => {
        this.isUpdating = false;
        if (error.status === 401) {
          this.close();
          return;
        }
      },
    });
  }

  private saveJobData() {
    const job = this.job;
    const jobData: any = deepClone(job);
    delete jobData.enterpriseDepartmentUsers;
    delete jobData.dynamicData.raId;
    delete jobData.dynamicData.paId;
    delete jobData.dynamicData.asId;
    jobData.enterpriseDepartmentUserIds = job.enterpriseDepartmentUsers ? job.enterpriseDepartmentUsers.map((u) => u.id) : [];
    return this.jobApiService.update(job.id, jobData);
  }

  private saveCompanyData() {
    const companyData: any = deepClone(this.companyData);
    return this.companyApiService.update(companyData.id, companyData);
  }
}
